import { type Me, ONBOARDING_STEPS, type OnboardingStep } from '@feral/shared/client'
import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { api, standalone } from './api.js'
import { home } from './prefs.js'
import { useSession } from './session.js'

/**
 * Onboarding (FR-ACC-06, bugs.md): the rules, your profile, the app on your home screen, then
 * notifications. Where you are comes first of all, before the age gate (the region decides so much). The account remembers the step it's on (`Me.onboarding`), so wherever it stops,
 * a reload, the installed app or a visit days later opens on that step again until it's done.
 */

/** Everything from where you are to the last primer, as the step counter counts it. */
export type SignupStep = 'location' | 'age' | 'account' | OnboardingStep
const NAMES: Record<SignupStep, string> = {
  location: 'Your location',
  age: 'Your age',
  account: 'Your account',
  rules: 'The rules',
  profile: 'Your profile',
  install: 'The app',
  notify: 'Notifications',
}
/** The steps this device goes through: the installed app has no install step. */
export function signupSteps(): { step: SignupStep; name: string }[] {
  return (['location', 'age', 'account', ...ONBOARDING_STEPS] as SignupStep[])
    .filter((s) => s !== 'install' || !standalone())
    .map((step) => ({ step, name: NAMES[step] }))
}

/** Each step's screen. */
export function stepRoute(step: OnboardingStep) {
  if (step === 'profile') return { to: '/you/profile', search: { first: true } } as const
  return { to: `/${step}` } as const
}

/**
 * Where an account opens (bugs.md): on its onboarding step until that's done; then a Pending member on
 * the Gate, where their way in is (the clips, who can vet them); everyone else on the map.
 */
export function openingRoute(me: Me) {
  if (me.onboarding) return stepRoute(me.onboarding)
  if (me.state === 'pending') return { to: '/gate' } as const
  return null
}

/** `from` is done or skipped: on to the step after it, or to the map once it's over. */
export function useOnboardingNext() {
  const nav = useNavigate()
  return useCallback(
    (from: OnboardingStep) => {
      const { me, set } = useSession.getState()
      // Not onboarding (the install screen from Settings, say): back to where they live.
      if (!me?.onboarding) return nav({ to: home() })
      // The step after this one, but never one behind where the account is already (accepting the
      // rules moves it on by itself; a second tab may be ahead).
      let at = Math.max(ONBOARDING_STEPS.indexOf(from) + 1, ONBOARDING_STEPS.indexOf(me.onboarding))
      // Already the installed app: nothing to install.
      if (ONBOARDING_STEPS[at] === 'install' && standalone()) {
        api.markInstalled().catch(() => {})
        at++
      }
      const next: OnboardingStep | undefined = ONBOARDING_STEPS[at]
      const after = { ...me, onboarding: next ?? null }
      set(after)
      api.onboarding(next ?? 'done').catch(() => {})
      nav(openingRoute(after) ?? { to: home() })
    },
    [nav],
  )
}
